/**
 * Responsibility: assemble the generation chain for one seed.
 *
 * The biome field, the heightfield, and the tile content builder are all seeded, and all three have to
 * be seeded *the same*. Building them in one place makes that impossible to get wrong, and gives the
 * rest of the application a single thing to replace when the seed changes.
 *
 * ## Why this exists as a module
 *
 * Before saving, the chain was constructed inline during startup and never changed. Loading a save and
 * starting a new world both replace the seed, so the chain became something built more than once —
 * and the moment there are two call sites, the wiring is logic rather than bootstrap.
 *
 * Having it here also makes determinism directly testable: two worlds from the same seed must produce
 * byte-identical tiles, which is the property the whole save format rests on.
 *
 * Contains no Three.js and no DOM. It produces plain geometry data; the render layer consumes it.
 */

import { createBiomeField } from './biomeField.js';
import { createHeightfield } from './heightfield.js';
import { createTileContent } from './tileContent.js';
import { createPalette } from './palette.js';
import { BIOMES } from './biomes/index.js';
import { normalizeSeed } from './rng.js';
import { createSurveyor } from '../railway/surveyor.js';
import { createCorridor } from '../railway/corridor.js';
import { createCorridorProfile } from '../railway/corridorProfile.js';
import { createTrackClearance } from '../railway/trackClearance.js';
import { WORLD, RAILWAY } from '../config.js';

/**
 * Survey a route through a world and build the corridor that shapes ground around it.
 *
 * Kept here rather than in `main.js` because **both threads have to build the identical route**. The worker
 * constructs its own chain from the seed and nothing is shared between them, exactly as it already does for
 * the noise fields — so the assembly has to live in the one place both call.
 *
 * @param {number} seed - Already normalised.
 * @param {object} biomeField
 * @param {object} natural - Heightfield with no corridor, for surveying against.
 * @returns {{surveyor: object, deform: Function, prepare: Function}}
 */
function createRoute(seed, biomeField, natural) {
  const sampleWeights = (x, z) => biomeField.sample(x, z);

  const weightOf = (id, x, z) => {
    for (const entry of biomeField.sample(x, z)) {
      if (entry.id === id) return entry.weight;
    }
    return 0;
  };

  const profile = createCorridorProfile({ sampleWeights });

  const surveyor = createSurveyor({
    seed,
    naturalGround: (x, z) => natural.sampleElevation(x, z),
    aimBelow: (x, z) => profile.aimBelowAt(x, z),

    /*
      Steering, read thousands of units ahead — so it has to be a smooth long-wavelength quantity. Biome
      weights are; terrain height is not, which is why steering on height was measured to be useless against
      2,600-unit ridges. Mountains are held at a target weight rather than avoided, so the line runs along the
      foot of a range and the peaks stay in view.
    */
    cost: (x, z) =>
      Math.abs(weightOf('mountains', x, z) - RAILWAY.MOUNTAIN_TARGET) + weightOf('mesa', x, z) * 0.5,
  });

  const corridor = createCorridor({ surveyor, profile });

  return {
    surveyor,
    deform: (x, z, elevation) => corridor.deform(x, z, elevation),

    /**
     * Walk the route out past a distance before any terrain near it is built.
     *
     * **The one invariant this whole mode rests on.** `surveyor.nearest` only sees chain already walked, the
     * heightfield caches lattice elevations with no way to invalidate them, and the worker walks its own
     * chain — so a tile built before the route reached it would disagree with the same tile built later, or
     * with the same tile built on the other thread. See `RAILWAY.PREPARE_MARGIN`.
     */
    prepare(distance) {
      surveyor.prepare(distance + RAILWAY.PREPARE_MARGIN);
    },
  };
}

/**
 * Build the generation chain for a seed.
 *
 * @param {object} options
 * @param {number|string} options.seed - Raw seed. Numbers and phrases both work; `normalizeSeed` is
 *   the authority on turning one into the integer the noise fields use.
 * @param {Array<object>} [options.biomes] - Defaults to the full registry. Injectable for tests.
 * @returns {object} The chain, plus the normalised seed so callers can display and save it.
 */
export function createWorld({ seed, biomes = BIOMES, railway = false } = {}) {
  const normalizedSeed = normalizeSeed(seed);

  const biomeField = createBiomeField({ seed: normalizedSeed, biomes });
  const sampleWeights = (x, z) => biomeField.sample(x, z);

  /*
    **Two heightfields when there is a railway, and the dependency has to stay one way round.** The route is
    surveyed against *natural* ground; the corridor is derived from the route; the drawn ground is natural
    ground with the corridor applied. Surveying against the shaped ground would be circular.

    Null when flying, where the second one is the only one and nothing is shaped.
  */
  const natural = railway
    ? createHeightfield({ seed: normalizedSeed, sampleWeights, biomes })
    : null;

  const route = railway ? createRoute(normalizedSeed, biomeField, natural) : null;

  /*
    Keeps every biome's structures off the rails. Narrower than the corridor on purpose: the corridor shapes
    ground hundreds of units out to blend a cutting into a hillside, and a wooded cutting is a fine thing —
    what has to stay clear is the formation and a little either side. See `railway/trackClearance.js`.
  */
  const trackClearance = route === null ? null : createTrackClearance({ surveyor: route.surveyor });

  /*
    One heightfield for the whole world. Terrain geometry and the flight model's terrain floor both
    read from it, so they cannot disagree about where the ground is.
  */
  const heightfield = createHeightfield({
    seed: normalizedSeed,
    sampleWeights,
    biomes,
    deform: route === null ? null : route.deform,
  });

  const content = createTileContent({
    seed: normalizedSeed,
    size: WORLD.TILE_SIZE,
    resolution: WORLD.TILE_RESOLUTION,
    heightfield,
    palette: createPalette(biomes),
    biomeField,
    biomes,
    reserved: trackClearance,
  });

  return {
    /** The integer actually used for generation. This is what gets saved and displayed. */
    seed: normalizedSeed,

    biomeField,
    heightfield,

    /**
     * The railway, or null in a world with no railway in it.
     *
     * Exposes the surveyed route for the train to ride and for the track to be drawn from, and `prepare` for
     * walking it out ahead of terrain. Nothing else in the world knows it is here.
     */
    railway: route === null ? null : { surveyor: route.surveyor, prepare: route.prepare },

    /**
     * The *drawn* surface height, not the continuous field.
     *
     * The flight floor and the camera both have to agree with the wireframe that is on screen, which
     * is sampled at tile vertices — see `heightfield.sampleSurface`.
     *
     * @param {number} x
     * @param {number} z
     * @returns {number}
     */
    sampleGround(x, z) {
      return heightfield.sampleSurface(x, z);
    },

    /**
     * Build one tile's geometry.
     *
     * @param {number} originX
     * @param {number} originZ
     * @returns {object} Shaped for `tileManager`'s `buildTile` callback.
     */
    buildTile(originX, originZ) {
      return content.build(originX, originZ);
    },
  };
}

/**
 * A seed from the current time, for "give me somewhere new".
 *
 * Not cryptographic and does not need to be — it only has to differ from the last one a player saw.
 *
 * @returns {number}
 */
export function randomSeed() {
  return normalizeSeed(Date.now() ^ Math.floor(Math.random() * 0xffffffff));
}
